import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Receipt } from "lucide-react";
import { api, money, won, fmtKST, previewUrl, FILL_RULES } from "@/lib/api";
import { LimitedTag } from "@/components/LimitedTag";
import { StatusBadge, FillBar, EmptyState } from "@/components/Bits";
import { UpcomingDrops } from "@/components/UpcomingDrops";

const FILTERS = [
  { v: "open", label: "Open" },
  { v: "closed", label: "Closed" },
  { v: "", label: "All" },
];

const closingSoon = (iso) => {
  const ms = new Date(iso) - Date.now();
  return ms > 0 && ms <= 86400000;
};

const CloseDate = ({ iso, testId }) => (
  <div data-testid={testId} className={closingSoon(iso) ? "font-bold text-red-400" : ""}>Claims close {fmtKST(iso)}</div>
);

const OpenMembers = ({ d }) => (
  <div data-testid={`drop-card-open-members-${d.id}`} className="mt-3">
    <div className="text-[11px] uppercase tracking-widest text-zinc-500">Set #{d.current_set} · {d.current_set_open.length} open</div>
    <div className="mt-1.5 flex flex-wrap gap-1">
      {d.current_set_open.map((m) => <span key={m} className="rounded-md bg-emerald-500/10 border border-emerald-500/25 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-200">{m}</span>)}
    </div>
    {d.next_set_needed > 0 && (
      <div data-testid={`drop-card-next-set-${d.id}`} className="mt-1.5 text-[11px] text-pink-200/90">
        <span className="font-semibold">{d.next_set_needed} more {d.next_set_needed === 1 ? "claim" : "claims"}</span> to open Set #{d.current_set + 1}
      </div>
    )}
  </div>
);

const DropCard = ({ d, i }) => {
  const full = d.filled_slots >= d.total_slots;
  const img = previewUrl(d);
  return (
    <Link
      to={`/drops/${d.id}`}
      data-testid={`drop-card-${d.id}`}
      style={{ animationDelay: `${Math.min(i, 9) * 50}ms` }}
      className="rise group flex flex-col rounded-2xl border border-white/5 bg-zinc-900/70 overflow-hidden transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-pink-500/40"
    >
      {img && <img src={img} alt="" className="h-40 w-full object-cover border-b border-white/5" />}
      <div className="flex flex-col flex-1 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge testId={`drop-card-status-${d.id}`} status={d.status === "open" && d.claims_closed ? "closed" : d.status === "open" && full ? "full" : d.status} label={d.status === "open" && d.claims_closed ? "Claims closed" : d.status === "open" && full ? "Full" : undefined} />
        {d.round_type && <span className="text-xs rounded-full bg-pink-500/15 text-pink-200 px-2 py-0.5 font-semibold">{d.round_type}</span>}
        {d.album_type && <span className="text-xs rounded-full bg-violet-500/15 text-violet-200 px-2 py-0.5 font-semibold">{d.album_type}</span>}
        {d.bonus_items && <span data-testid={`drop-bonus-tag-${d.id}`} className="inline-flex items-center gap-1 text-xs rounded-full bg-amber-400/15 text-amber-200 px-2 py-0.5 font-semibold"><Gift className="h-3 w-3" />Bonus</span>}
        {d.limited && <LimitedTag testId={`drop-limited-tag-${d.id}`} />}
        {d.receipts?.length > 0 && (
          <span data-testid={`drop-receipt-tag-${d.id}`} className="inline-flex items-center gap-1 text-xs rounded-full bg-emerald-500/15 text-emerald-200 px-2 py-0.5 font-semibold">
            <Receipt className="h-3 w-3" />Receipt posted
          </span>
        )}
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-4 group-hover:text-pink-200 transition-colors">{d.store || d.title}</h3>
      <div className="text-xs text-zinc-500 mt-2 space-y-0.5">
        {FILL_RULES[d.fill_rule] && <div>Fill rule: {FILL_RULES[d.fill_rule].label}{d.limited ? " · first come" : ""}</div>}
        {d.claims_close && <CloseDate iso={d.claims_close} testId={`drop-card-close-${d.id}`} />}
      </div>
      {d.current_set && d.current_set_open?.length > 0 && <OpenMembers d={d} />}
      <div className="mt-auto pt-6">
        <div className="flex justify-between text-sm mb-3">
          <span className="text-zinc-400">{d.num_sets} sets × {d.members.length} members</span>
          <span className="font-mono font-semibold text-right" data-testid={`drop-card-price-${d.id}`}>{money(d.price_per_member)}{d.price_won != null && <span className="text-zinc-400"> · {won(d.price_won)}</span>}<span className="text-zinc-500 text-xs"> /{d.fill_rule === "unit" ? "unit" : "member"}</span></span>
        </div>
        <FillBar filled={d.filled_slots} total={d.total_slots} />
      </div>
      </div>
    </Link>
  );
};

const SpecialCard = ({ sd, i }) => (
  <Link
    to={`/special/${sd.id}`}
    data-testid={`special-card-${sd.id}`}
    style={{ animationDelay: `${Math.min(i, 9) * 50}ms` }}
    className="rise group flex flex-col rounded-2xl border border-amber-400/20 bg-zinc-900/70 p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-400/50"
  >
    <div className="flex flex-wrap items-center gap-2">
      <StatusBadge testId={`special-card-status-${sd.id}`} status={sd.status === "open" && sd.claims_closed ? "closed" : sd.status} label={sd.status === "open" && sd.claims_closed ? "Claims closed" : undefined} />
      <span className="text-xs rounded-full bg-amber-500/15 text-amber-200 px-2 py-0.5 font-semibold">Special</span>
      {sd.item_types.map((t) => <span key={t} className="text-xs rounded-full bg-white/5 text-zinc-300 px-2 py-0.5">{t}</span>)}
    </div>
    <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-4 group-hover:text-amber-200 transition-colors">{sd.name}</h3>
    {sd.claims_close && <div className="text-xs text-zinc-500 mt-2"><CloseDate iso={sd.claims_close} testId={`special-card-close-${sd.id}`} /></div>}
    <div className="mt-auto pt-6">
      <div className="flex justify-between text-sm mb-3">
        <span className="text-zinc-400">{sd.items.length} items</span>
        <span className="font-mono text-xs text-zinc-400">{sd.total_quantity - sd.total_claimed} left</span>
      </div>
      <FillBar filled={sd.total_claimed} total={sd.total_quantity} unit="claimed" />
    </div>
  </Link>
);

const SORTS = [
  { v: "newest", label: "Newest" },
  { v: "closing", label: "Closing Soon" },
  { v: "coming", label: "Coming Soon" },
  { v: "fancall", label: "Fancall" },
];

const closeKey = (x) => (x.claims_close && new Date(x.claims_close) > Date.now() ? new Date(x.claims_close).getTime() : Infinity);
const arrange = (list, sort) => {
  if (sort === "closing") return [...list].sort((a, b) => closeKey(a) - closeKey(b));
  return [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
};

const SortBar = ({ sort, setSort }) => (
  <div className="mt-6 flex flex-wrap gap-2" data-testid="drops-sort-bar">
    {SORTS.map((s) => (
      <button key={s.v} type="button" data-testid={`drops-sort-${s.v}`} onClick={() => setSort(s.v)}
        className={`px-3.5 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-colors duration-200 ${sort === s.v ? "border-pink-400/60 bg-pink-500/15 text-pink-100" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"}`}>
        {s.label}
      </button>
    ))}
  </div>
);

export default function Drops() {
  const [filter, setFilter] = useState("open");
  const [sort, setSortState] = useState(() => {
    const saved = localStorage.getItem("drops-sort");
    return SORTS.some((s) => s.v === saved) ? saved : "newest";
  });
  const setSort = (v) => {
    localStorage.setItem("drops-sort", v);
    setSortState(v);
  };
  const [drops, setDrops] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    setDrops(null);
    const params = filter ? { status: filter } : {};
    api.get("/drops", { params }).then((r) => setDrops(r.data.filter((d) => !d.scheduled))).catch(() => setDrops([]));
    api.get("/special-drops", { params }).then((r) => setSpecials(r.data)).catch(() => setSpecials([]));
  }, [filter, tick]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Open Drops</h1>
          <p className="text-zinc-400 mt-2 text-sm md:text-base">Pick a drop, claim a member, and the next open set fills.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-white/5 bg-zinc-900 p-1 self-start">
          {FILTERS.map((f) => (
            <button
              key={f.label}
              data-testid={`drops-filter-${f.label.toLowerCase()}`}
              onClick={() => setFilter(f.v)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${filter === f.v ? "bg-white text-zinc-950" : "text-zinc-400 hover:text-white"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <SortBar sort={sort} setSort={setSort} />
      {sort === "coming" ? <UpcomingDrops onOpened={refresh} emptyHint /> : <DropLists drops={drops} specials={specials} sort={sort} onOpened={refresh} />}
    </div>
  );
}

const DropLists = ({ drops, specials: allSpecials, sort, onOpened }) => {
  const fancall = sort === "fancall";
  const list = drops && arrange(fancall ? drops.filter((d) => d.has_fancall) : drops, sort);
  const specials = fancall ? [] : arrange(allSpecials, sort);
  return (
    <>
      {specials.length > 0 && <h2 className="font-display text-base md:text-lg font-bold mt-8">Store Drops</h2>}
      <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-5 ${specials.length ? "mt-3" : "mt-8"}`} data-testid="drops-list">
        {list === null && <div className="text-zinc-500 text-sm">Loading…</div>}
        {list?.map((d, i) => <DropCard key={d.id} d={d} i={i} />)}
      </div>
      {list && list.length === 0 && !specials.length && (fancall
        ? <EmptyState testId="drops-fancall-empty" title="No Fancall rounds here" hint="Try All to see past Fancall rounds." />
        : <EmptyState testId="drops-empty" title="No drops here yet" hint="Admins will post new drops soon." />)}
      {list && list.length === 0 && specials.length > 0 && <EmptyState testId="store-drops-empty" title="No store drops here yet" />}
      {specials.length > 0 && (
        <section className="mt-10" data-testid="special-drops-section">
          <h2 className="font-display text-base md:text-lg font-bold">Special Drops</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">{specials.map((sd, i) => <SpecialCard key={sd.id} sd={sd} i={i} />)}</div>
        </section>
      )}
      {!fancall && <div className="mt-2"><UpcomingDrops onOpened={onOpened} /></div>}
    </>
  );
};
